import { oc } from "@orpc/contract";
import { z } from "zod";
import { ArActivityTimelineItemSchema } from "./ar-customer-profile.contract";

/**
 * AR Customer Communications RPC Contract
 *
 * Surfaces customer-level collection status + recent comms activity to the
 * AR reports view (chip + popover). The aging endpoints stay focused on
 * balances; this contract handles the comms slice.
 */

export const CustomerCollectionStatusSchema = z.enum([
  "current",
  "promised_to_pay",
  "disputed",
  "in_collections",
  "do_not_contact",
]);

export type CustomerCollectionStatus = z.infer<
  typeof CustomerCollectionStatusSchema
>;

/** One row per customer requested in getCommsSummary. */
export const ArCustomerCommsSummarySchema = z.object({
  customerId: z.string().uuid(),
  collectionStatus: CustomerCollectionStatusSchema,
  collectionStatusUpdatedAt: z.string().nullable(),
  /** Most recent timeline timestamp across all sources; null if no activity. */
  lastContactAt: z.string().nullable(),
  /** Last 5 timeline items, newest first. Same shape as customer profile. */
  recentActivity: z.array(ArActivityTimelineItemSchema),
});

export type ArCustomerCommsSummary = z.infer<
  typeof ArCustomerCommsSummarySchema
>;

export const GetArCustomerCommsSummaryInputSchema = z.object({
  customerIds: z.array(z.string().uuid()).min(1).max(200),
});

export const GetArCustomerCommsSummaryResponseSchema = z.object({
  summaries: z.array(ArCustomerCommsSummarySchema),
});

export const SetCollectionStatusInputSchema = z.object({
  customerId: z.string().uuid(),
  status: CustomerCollectionStatusSchema,
});

export const SetCollectionStatusResponseSchema = z.object({
  customerId: z.string().uuid(),
  collectionStatus: CustomerCollectionStatusSchema,
  collectionStatusUpdatedAt: z.string().nullable(),
});

/**
 * Update a customer's communication opt-outs. Both flags are optional so the
 * UI can toggle one at a time; only provided fields are applied. The response
 * always reflects the full resulting state.
 */
export const SetCommsExclusionsInputSchema = z.object({
  customerId: z.string().uuid(),
  excludeFromPaymentReminders: z.boolean().optional(),
  excludeFromInvoiceEmails: z.boolean().optional(),
});

export const SetCommsExclusionsResponseSchema = z.object({
  customerId: z.string().uuid(),
  excludeFromPaymentReminders: z.boolean(),
  excludeFromInvoiceEmails: z.boolean(),
});

export const AddCustomerNoteInputSchema = z.object({
  customerId: z.string().uuid(),
  note: z.string().min(1).max(4000),
});

export const AddCustomerNoteResponseSchema = z.object({
  eventId: z.string().uuid(),
  occurredAt: z.string(),
});

export const EditCustomerNoteInputSchema = z.object({
  eventId: z.string().uuid(),
  note: z.string().min(1).max(4000),
});

export const EditCustomerNoteResponseSchema = z.object({
  eventId: z.string().uuid(),
  updatedAt: z.string(),
});

export const DeleteCustomerNoteInputSchema = z.object({
  eventId: z.string().uuid(),
});

export const DeleteCustomerNoteResponseSchema = z.object({
  success: z.literal(true),
});

export const arCustomerCommsContracts = {
  getCommsSummary: oc
    .input(GetArCustomerCommsSummaryInputSchema)
    .output(GetArCustomerCommsSummaryResponseSchema)
    .route({ method: "GET" }),
  setCollectionStatus: oc
    .input(SetCollectionStatusInputSchema)
    .output(SetCollectionStatusResponseSchema)
    .route({ method: "POST" }),
  setCommsExclusions: oc
    .input(SetCommsExclusionsInputSchema)
    .output(SetCommsExclusionsResponseSchema)
    .route({ method: "POST" }),
  addCustomerNote: oc
    .input(AddCustomerNoteInputSchema)
    .output(AddCustomerNoteResponseSchema)
    .route({ method: "POST" }),
  editCustomerNote: oc
    .input(EditCustomerNoteInputSchema)
    .output(EditCustomerNoteResponseSchema)
    .route({ method: "POST" }),
  deleteCustomerNote: oc
    .input(DeleteCustomerNoteInputSchema)
    .output(DeleteCustomerNoteResponseSchema)
    .route({ method: "POST" }),
} as const;

export type GetArCustomerCommsSummaryInput = z.infer<
  typeof GetArCustomerCommsSummaryInputSchema
>;
export type GetArCustomerCommsSummaryResponse = z.infer<
  typeof GetArCustomerCommsSummaryResponseSchema
>;
export type SetCollectionStatusInput = z.infer<
  typeof SetCollectionStatusInputSchema
>;
export type SetCollectionStatusResponse = z.infer<
  typeof SetCollectionStatusResponseSchema
>;
export type SetCommsExclusionsInput = z.infer<
  typeof SetCommsExclusionsInputSchema
>;
export type SetCommsExclusionsResponse = z.infer<
  typeof SetCommsExclusionsResponseSchema
>;
export type AddCustomerNoteInput = z.infer<typeof AddCustomerNoteInputSchema>;
export type AddCustomerNoteResponse = z.infer<
  typeof AddCustomerNoteResponseSchema
>;
export type EditCustomerNoteInput = z.infer<typeof EditCustomerNoteInputSchema>;
export type EditCustomerNoteResponse = z.infer<
  typeof EditCustomerNoteResponseSchema
>;
export type DeleteCustomerNoteInput = z.infer<
  typeof DeleteCustomerNoteInputSchema
>;
export type DeleteCustomerNoteResponse = z.infer<
  typeof DeleteCustomerNoteResponseSchema
>;
